import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type {
  EvaluatedRoof,
  Roof,
  RoofSystem,
  RoofSystemInternal,
  RoofSystemTableInternal,
} from "building-types/roofs/roofSystemTypes";
import type { RWTable, Table } from "common/data/table";
import { resolveInstanceScopedEntityId } from "doc/entity/componentInstanceEntityResolve";
import { isVirtualRoofBlock, resolveBlockRefInstanceId, resolveRoofBlockIds } from "doc/roofSystemTools";
import { buildEdgeModifiersByParentId, type EdgeModifiersByParentId } from "doc/utils/modifierUtils";
import { shouldGenerateEntity } from "../../utils/entityIsolation";
import { roofSystemToInternal } from "../multiGableRoofSystemToInternal";
import { nonEmptyGableSubRoof } from "./roofSubRoofFilters";

export function getRoofLevelId(roof: Roof, blockLevelIds: { [key: string]: string }, levels: Table<Level>): string {
  const { blocks: roofBlocks } = roof;

  if (isVirtualRoofBlock(roofBlocks)) {
    const { levelUsage } = roofBlocks;
    return resolveInstanceScopedEntityId({
      path: levelUsage,
      table: levels,
      fallbackId: levelUsage.entityId,
    });
  }

  if (Array.isArray(roofBlocks)) {
    if (!(roofBlocks.length > 0)) {
      console.assert(false, "Roof must have more than one block");
    }
    return blockLevelIds[resolveBlockRefInstanceId(roofBlocks[0], blockLevelIds)];
  }

  return blockLevelIds[resolveBlockRefInstanceId(roofBlocks, blockLevelIds)];
}

export function getSortedRoofs(
  roofSystem: RoofSystemInternal,
  filterFunc?: (x: EvaluatedRoof) => boolean,
): EvaluatedRoof[] {
  let subRoofs = Object.values(roofSystem.subRoofs);

  if (filterFunc) {
    subRoofs = subRoofs.filter(filterFunc);
  }

  return subRoofs.sort((a, b) => a.sortIndex - b.sortIndex);
}

export function getRoofSystemLevelId(
  roofSystem: RoofSystemInternal,
  blockLevelIds: { [key: string]: string },
  levels: Table<Level>,
): string | null {
  const sortedSubRoofs = getSortedRoofs(roofSystem, nonEmptyGableSubRoof);
  return sortedSubRoofs.length > 0 ? getRoofLevelId(sortedSubRoofs[0], blockLevelIds, levels) : null;
}

export interface FilterAndGetRoofSystemsOpts {
  unfilteredRoofSystems: Table<RoofSystem>;
  unfilteredRoofs: Table<EvaluatedRoof>;
  levels: Table<Level>;
  blockPerimeters: BlockPerimeters;
  blockLevelIds: { [blockId: string]: string };
  mirror: boolean;
  mirrorTranslationX: number;
  modifiers: Table<GeneratedModifier>;
  edgeModifiers: Table<EdgeModifier>;
  idToParents: Table<readonly string[]>;
  isolatedEntity?: DocEntityHandle;
  modifiersByInstanceChain: Table<Table<GeneratedModifier>>;
}

function roofSystemTableToInternal(
  roofSystems: Table<RoofSystem>,
  roofs: Table<EvaluatedRoof>,
  mirror: boolean,
  mirrorTranslationX: number,
  modifiers: Table<GeneratedModifier>,
  edgeModifiersByParentId: EdgeModifiersByParentId,
  modifiersByInstanceChain: Table<Table<GeneratedModifier>>,
): RoofSystemTableInternal {
  const convertedRoofSystems: RWTable<RoofSystemInternal> = {};

  for (const roofSystemId in roofSystems) {
    const roofSystem = roofSystems[roofSystemId];
    convertedRoofSystems[roofSystemId] = roofSystemToInternal(
      roofSystem,
      roofs,
      mirror,
      mirrorTranslationX,
      modifiers,
      edgeModifiersByParentId,
      modifiersByInstanceChain,
    );
  }

  return convertedRoofSystems;
}

function filterDisabledRoofSystems(
  roofSystems: Table<RoofSystem>,
  roofs: Table<EvaluatedRoof>,
  levels: Table<Level>,
  blockPerimeters: BlockPerimeters,
  blockLevelIds: { [key: string]: string },
  idToParents: Table<readonly string[]>,
  isolatedEntity?: DocEntityHandle,
): [Table<RoofSystem>, Table<EvaluatedRoof>] {
  // 1. Filter roofs table
  const filteredRoofs: { [roofId: string]: EvaluatedRoof } = {};
  for (const roofId in roofs) {
    let roof = roofs[roofId];
    if (!roof) {
      continue;
    }

    if (roof.disabled || !shouldGenerateEntity(roofId, idToParents, isolatedEntity)) {
      continue;
    }

    // Filter out VirtualRoofBlocks that reference ephemeral levels
    // (ephemeral levels don't exist during isolated component generation)
    // Only filter when in isolated mode
    if (isolatedEntity && isVirtualRoofBlock(roof.blocks)) {
      const { componentInstanceChain } = roof.blocks.levelUsage;
      if (componentInstanceChain && componentInstanceChain.length > 0) {
        continue;
      }
    }

    if (Array.isArray(roof.blocks)) {
      const filteredBlockIds = resolveRoofBlockIds(roof.blocks, blockPerimeters).filter((id) => {
        return blockPerimeters[id] && levels[blockLevelIds[id]];
      });
      roof = {
        ...roof,
        blocks: filteredBlockIds,
      };
    }

    if (nonEmptyGableSubRoof(roof)) {
      filteredRoofs[roofId] = roof;
    }
  }

  // 2. Filter roof systems
  const filteredRoofSystems: { [roofSystemId: string]: RoofSystem } = {};
  for (const roofSystemId in roofSystems) {
    const roofSystem = roofSystems[roofSystemId];
    if (!roofSystem) {
      continue;
    }

    if (roofSystem.disabled) {
      continue;
    }

    const subroofs = Object.values(filteredRoofs).filter((roof) => roof.parentId === roofSystemId);

    if (subroofs.length === 0) {
      continue;
    }

    filteredRoofSystems[roofSystemId] = roofSystem;
  }

  return [filteredRoofSystems, filteredRoofs];
}

export function filterAndGetRoofSystems({
  unfilteredRoofSystems,
  unfilteredRoofs,
  levels,
  blockPerimeters,
  blockLevelIds,
  mirror,
  mirrorTranslationX,
  modifiers,
  edgeModifiers,
  idToParents,
  isolatedEntity,
  modifiersByInstanceChain,
}: FilterAndGetRoofSystemsOpts) {
  const [roofSystems, inputRoofs] = filterDisabledRoofSystems(
    unfilteredRoofSystems,
    unfilteredRoofs,
    levels,
    blockPerimeters,
    blockLevelIds,
    idToParents,
    isolatedEntity,
  );

  const edgeModifiersByParentId = buildEdgeModifiersByParentId(edgeModifiers);

  return roofSystemTableToInternal(
    roofSystems,
    inputRoofs,
    mirror,
    mirrorTranslationX,
    modifiers,
    edgeModifiersByParentId,
    modifiersByInstanceChain,
  );
}
